import { z } from "zod";

/**
 * Common validation patterns and utilities
 */

/**
 * UUID validation schema
 */
export const uuidSchema = z.string().uuid("Invalid UUID format");

/**
 * Email validation schema
 */
export const emailSchema = z
  .string()
  .email("Please enter a valid email address")
  .max(255, "Email must be less than 255 characters")
  .trim()
  .toLowerCase();

/**
 * Name validation schema (2-255 characters)
 */
export const nameSchema = z
  .string()
  .min(2, "Name must be at least 2 characters long")
  .max(255, "Name must be less than 255 characters")
  .trim();

/**
 * Required name validation schema (1-255 characters)
 */
export const requiredNameSchema = z
  .string()
  .min(1, "Name is required")
  .max(255, "Name must be less than 255 characters")
  .trim();

/**
 * Person name validation schema (first name / last name).
 * Single source of truth for user name constraints across all forms
 * (signup, profile update, admin user edit, invitations, etc.).
 * Aligned with backend UserUpdate / UserRegisterWithInvite (min 2, max 30).
 */
export const personNameSchema = z
  .string()
  .min(2, "Name must be at least 2 characters")
  .max(30, "Name must be less than 30 characters")
  .trim();

/**
 * Optional description validation schema
 */
export const optionalDescriptionSchema = z
  .string()
  .min(2, "Description must be at least 2 characters long")
  .max(255, "Description must be less than 255 characters")
  .trim()
  .optional()
  .or(z.literal(""));

/**
 * Compact phone for backend UserUpdate (maxLength 15): keep leading `+` and digits only.
 * Formatting characters (spaces, parentheses, dashes) are stripped.
 */
export function normalizePhoneInput(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  return hasPlus ? `+${digits}` : digits;
}

/** Backend UserUpdate / UserRegister phone maxLength. */
const PHONE_STORED_MAX = 15;
/** Generous cap on decorated UI input; length is enforced on the normalized form. */
const PHONE_INPUT_MAX = 32;

/**
 * Phone number validation schema.
 * Allows country code and common formatting (+, spaces, parentheses, dashes).
 * Length is checked on the normalized value (max 15) to match backend String(15);
 * output is normalized so the submitted payload always fits.
 */
export const phoneSchema = z
  .string()
  .trim()
  .max(PHONE_INPUT_MAX, "Phone number is too long")
  .regex(/^[0-9+\-()\s]*$/, "Please enter a valid phone number")
  .refine(
    (val) => val === "" || normalizePhoneInput(val).length <= PHONE_STORED_MAX,
    {
      message:
        "Phone number must be at most 15 characters (formatting is ignored)",
    },
  )
  .transform((val) => (val === "" ? "" : normalizePhoneInput(val)))
  .optional()
  .or(z.literal(""));

/**
 * Password validation schema with security requirements
 */
export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/\d/, "Password must contain at least one number")
  .regex(
    /[^A-Za-z0-9]/,
    "Password must contain at least one special character",
  );

/** Shared FieldTooltip copy — keep in sync with `passwordSchema` rules above. */
export const PASSWORD_REQUIREMENTS_HINT =
  "Must be 8+ characters with uppercase, lowercase, number, and special character";

/**
 * Generic validation result type
 */
export interface ValidationResult<T> {
  success: boolean;
  data: T | null;
  errors: Record<string, string[]> | null;
}

/**
 * Form errors type
 */
export type FormErrors = Record<string, string[]>;

/**
 * Generic form validation function
 */
export function validateForm<T>(
  schema: z.ZodType<T>,
  data: unknown,
): ValidationResult<T> {
  try {
    const validationResult = schema.safeParse(data);

    if (validationResult.success) {
      return {
        success: true,
        data: validationResult.data,
        errors: null,
      };
    }

    const errors: FormErrors = {};

    validationResult.error.issues.forEach((issue) => {
      const path = issue.path[0]?.toString() || "_form";

      if (!errors[path]) {
        errors[path] = [];
      }

      errors[path].push(issue.message);
    });

    return {
      success: false,
      data: null,
      errors,
    };
  } catch (err) {
    console.error("Validation error:", err);

    return {
      success: false,
      data: null,
      errors: {
        _form: ["Form validation failed unexpectedly"],
      },
    };
  }
}
